import * as Sentry from "@sentry/react";
import { getEffectiveConsent, getStoredReplayRate } from "@/lib/cookieConsent";

/** Évite une double initialisation lors d'un consentement donné en cours de session. */
let initialized = false;

/**
 * Configuration Sentry pour monitoring d'erreurs en production.
 *
 * RGPD : rien n'est initialisé tant que la catégorie « Mesure et diagnostic »
 * n'est pas acceptée. Sans accord : aucune erreur, aucune trace, aucun replay.
 */
export function initSentry() {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    // Avertir seulement en développement
    if (import.meta.env.DEV) {
      console.warn("Sentry DSN not configured. Error monitoring disabled.");
    }
    return;
  }

  // Consentement obligatoire : pas d'accord = pas de mesure du tout.
  if (!getEffectiveConsent().diagnostics) return;

  // Taux de replay réglable en administration (0 = replay désactivé).
  const replayRate = getStoredReplayRate();
  const replayEnabled = replayRate > 0;

  initialized = true;

  Sentry.init({
    dsn,
    
    // Intégrations pour tracing distribué et monitoring performance
    integrations: [
      // Tracing automatique des interactions utilisateur
      Sentry.browserTracingIntegration({
        // Tracer les navigations
        enableInp: true,
      }),
      // Le Replay est ajouté après coup (chargement différé, voir plus bas)
    ],


    // Performance monitoring - 20% des transactions
    tracesSampleRate: 0.2,
    
    // Performance profiling - 20% des traces
    profilesSampleRate: 0.2,

    // Replay configuration (0 tant que le consentement/réglage ne l'autorise pas)
    replaysSessionSampleRate: replayEnabled ? replayRate : 0,
    replaysOnErrorSampleRate: replayEnabled ? 1.0 : 0,


    // Environnement
    environment: import.meta.env.MODE,
    
    // Release tracking (optionnel)
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",

    // Filtres avant envoi
    beforeSend(event, hint) {
      // Ne pas envoyer les erreurs de dev en production
      if (import.meta.env.DEV) {
        console.log("Sentry event (dev):", event);
        return null;
      }

      // Filtrer les erreurs connues non critiques
      const error = hint.originalException;
      if (error instanceof Error) {
        // Ignorer les erreurs réseau temporaires
        if (error.message.includes("NetworkError") || 
            error.message.includes("Failed to fetch")) {
          return null;
        }
      }

      return event;
    },

    // Configuration des breadcrumbs (fil d'Ariane)
    beforeBreadcrumb(breadcrumb) {
      // Masquer les données sensibles dans les requêtes
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data?.url?.includes("password") || 
            breadcrumb.data?.url?.includes("token")) {
          breadcrumb.data = { ...breadcrumb.data, url: "[REDACTED]" };
        }
      }
      return breadcrumb;
    },

    // Ignorer certaines erreurs
    ignoreErrors: [
      // Erreurs de navigateur
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      // Erreurs d'extensions navigateur
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
    ],
  });

  // Replay chargé à la demande, hors du bundle principal : il ne pèse sur le
  // premier affichage que si l'administration l'a réellement activé (taux > 0).
  if (replayEnabled) {
    void (async () => {
      try {
        const replayIntegration = await Sentry.lazyLoadIntegration("replayIntegration");
        Sentry.getClient()?.addIntegration(
          replayIntegration({ maskAllText: true, blockAllMedia: true }),
        );
      } catch {
        // Replay indisponible : le monitoring d'erreurs continue normalement.
      }
    })();
  }
}


/**
 * Wrapper pour capturer les erreurs manuellement
 */
export function captureError(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    contexts: { custom: context },
  });
}

/**
 * Ajouter des tags utilisateur pour le contexte
 */
export function setUserContext(userId: string) {
  // Aucun e-mail envoyé à Sentry : seul l'identifiant.
  Sentry.setUser({ id: userId });
}

/**
 * Nettoyer le contexte utilisateur (logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Ajouter un breadcrumb personnalisé
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    level: "info",
    data,
  });
}

/**
 * Mesurer la performance d'une opération
 */
export async function measurePerformance<T>(
  name: string,
  operation: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      name,
      op: "custom",
    },
    async () => {
      try {
        return await operation();
      } catch (error) {
        captureError(error as Error, { operation: name });
        throw error;
      }
    }
  );
}
